import React, { useState, useEffect } from 'react'
import FileUpload from './FileUpload'
import { useGallery } from '../../contexts/GalleryContext'
import { getThumbnailUrl, getImageUrl } from '../../utils/imageUtils'
import { 
  Upload, 
  Search, 
  Trash2, 
  Edit, 
  Eye, 
  Plus, 
  Image as ImageIcon, 
  Video, 
  Filter,
  Save,
  X,
  Calendar,
  Tag,
  FolderOpen,
  Grid,
  List,
  Download,
  RotateCcw,
  Settings
} from 'lucide-react'

const GalleryManagement = () => {
  const { 
    galleryItems, 
    addGalleryItem, 
    updateGalleryItem, 
    deleteGalleryItem,
    loading 
  } = useGallery()
  
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [selectedItems, setSelectedItems] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [viewMode, setViewMode] = useState('grid')
  const [previewItem, setPreviewItem] = useState(null)
  const [imageFile, setImageFile] = useState(null) // Store the actual File object

  const [formData, setFormData] = useState({
    title: '',
    category: 'Events',
    type: 'image',
    url: '',
    description: '',
    tags: '',
    alt: '',
    isActive: true,
    featured: false
  })

  const categories = ['all', 'Events', 'Campus Life', 'Celebrations', 'Sports', 'Academic', 'Other']

  const filteredItems = galleryItems.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
    const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter
    
    return matchesSearch && matchesCategory
  })

  // Handle file upload - receives just the File object from FileUpload component
  const handleFileUpload = (file) => {
    if (file) {
      // Store the File object for later FormData creation
      setImageFile(file)
      
      // Create a preview URL
      const previewUrl = URL.createObjectURL(file)
      
      // Update form data with preview URL and type
      setFormData(prev => ({
        ...prev,
        url: previewUrl, // Keep the preview URL for display
        type: file.type.startsWith('video/') ? 'video' : 'image'
      }))
    } else {
      // File was removed
      setImageFile(null)
      setFormData(prev => ({
        ...prev,
        url: '',
        type: 'image'
      }))
    }
  }

  // CRUD Operations
  const handleCreate = () => {
    setEditingItem(null)
    setImageFile(null) // Reset image file
    setFormData({
      title: '',
      category: 'Events',
      type: 'image',
      url: '',
      description: '',
      tags: '',
      alt: '',
      isActive: true,
      featured: false
    })
    setIsModalOpen(true)
  }

  const handleEdit = (item) => {
    setEditingItem(item)
    setImageFile(null) // Reset image file (editing won't change the image by default)
    setFormData({
      title: item.title,
      category: item.category,
      type: item.type,
      url: item.url,
      description: item.description,
      tags: item.tags.join(', '),
      alt: item.alt,
      isActive: item.isActive,
      featured: item.featured
    })
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    // Cleanup blob URL if it exists
    if (formData.url && formData.url.startsWith('blob:')) {
      URL.revokeObjectURL(formData.url)
    }
    setIsModalOpen(false)
    setImageFile(null)
  }

  const handleSave = () => {
    if (!formData.title) {
      alert('Please fill in the title')
      return
    }

    // For new items, require an image file
    if (!editingItem && !imageFile) {
      alert('Please upload an image')
      return
    }

    // Create FormData for multipart upload
    const data = new FormData()
    
    // Append text fields
    data.append('title', formData.title)
    data.append('category', formData.category)
    data.append('description', formData.description)
    data.append('alt', formData.alt)
    data.append('isActive', formData.isActive)
    data.append('featured', formData.featured)
    
    // Append tags as a comma-separated string or JSON array
    const tagsArray = formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
    data.append('tags', JSON.stringify(tagsArray))
    
    // Append the image file if provided
    if (imageFile) {
      data.append('image', imageFile)
    }

    if (editingItem) {
      // For updates, if no new file, send JSON instead of FormData
      if (!imageFile) {
        const jsonData = {
          title: formData.title,
          category: formData.category,
          description: formData.description,
          alt: formData.alt,
          isActive: formData.isActive,
          featured: formData.featured,
          tags: tagsArray
        }
        updateGalleryItem(editingItem._id, jsonData)
      } else {
        updateGalleryItem(editingItem._id, data)
      }
    } else {
      // Add new item
      addGalleryItem(data)
    }

    // Cleanup blob URL if it exists
    if (formData.url && formData.url.startsWith('blob:')) {
      URL.revokeObjectURL(formData.url)
    }

    setIsModalOpen(false)
    setImageFile(null) // Reset image file
  }

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      deleteGalleryItem(id)
    }
  }

  const handleBulkDelete = () => {
    if (selectedItems.length === 0) return
    if (window.confirm(`Are you sure you want to delete ${selectedItems.length} selected items?`)) {
      selectedItems.forEach(id => deleteGalleryItem(id))
      setSelectedItems([])
    }
  }

  const handleBulkStatusToggle = () => {
    if (selectedItems.length === 0) return
    selectedItems.forEach(id => {
      const item = galleryItems.find(item => item._id === id)
      if (item) {
        updateGalleryItem(id, { isActive: !item.isActive })
      }
    })
    setSelectedItems([])
  }

  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    )
  }

  const handleSelectAll = () => {
    if (selectedItems.length === filteredItems.length) {
      setSelectedItems([])
    } else {
      setSelectedItems(filteredItems.map(item => item._id))
    }
  }

  const getStats = () => {
    const total = galleryItems.length
    const active = galleryItems.filter(item => item.isActive).length
    const featured = galleryItems.filter(item => item.featured).length
    const byCategory = categories.slice(1).reduce((acc, cat) => {
      acc[cat] = galleryItems.filter(item => item.category === cat).length
      return acc
    }, {})

    return { total, active, featured, byCategory }
  }

  const stats = getStats()

  return (
    <div className="p-6 bg-white min-h-screen">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Gallery Management</h1>
          <p className="text-gray-600">Manage your school's photo gallery and media content</p>
        </div>
        <button
          onClick={handleCreate}
          className="bg-primary-purple hover:bg-primary-magenta text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Add New Media
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-r from-primary-purple to-primary-magenta text-white p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm">Total Items</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
            <ImageIcon className="w-8 h-8 text-white/70" />
          </div>
        </div>
        <div className="bg-gradient-to-r from-primary-green to-primary-blue text-white p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm">Active</p>
              <p className="text-2xl font-bold">{stats.active}</p>
            </div>
            <Eye className="w-8 h-8 text-white/70" />
          </div>
        </div>
        <div className="bg-gradient-to-r from-primary-yellow to-primary-brown text-black p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-black/70 text-sm">Featured</p>
              <p className="text-2xl font-bold">{stats.featured}</p>
            </div>
            <Tag className="w-8 h-8 text-black/60" />
          </div>
        </div>
        <div className="bg-gradient-to-r from-primary-blue to-primary-green text-white p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm">Categories</p>
              <p className="text-2xl font-bold">{Object.keys(stats.byCategory).length}</p>
            </div>
            <FolderOpen className="w-8 h-8 text-white/70" />
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-gray-50 p-4 rounded-lg mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search by title, description, or tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
              />
            </div>
          </div>

        
          {/* View Mode Toggle */}
          <div className="flex bg-white border border-gray-300 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 ${viewMode === 'grid' 
                ? 'bg-primary-purple text-white' 
                : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 ${viewMode === 'list' 
                ? 'bg-primary-purple text-white' 
                : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedItems.length > 0 && (
        <div className="bg-primary-blue/10 border border-primary-blue/20 p-4 rounded-lg mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="text-primary-blue font-medium">
              <span className="font-bold">{selectedItems.length}</span> item(s) selected
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleBulkStatusToggle}
                className="bg-primary-blue hover:bg-primary-green text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Toggle Status
              </button>
              <button
                onClick={handleBulkDelete}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
              >
                Delete Selected
              </button>
              <button
                onClick={() => setSelectedItems([])}
                className="bg-primary-brown hover:bg-primary-brown/80 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gallery Items */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Gallery Items ({filteredItems.length})
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSelectAll}
              className="text-sm text-primary-purple hover:text-primary-magenta font-medium transition-colors"
            >
              {selectedItems.length === filteredItems.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>
        </div>

        {/* Gallery Grid/List View */}
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredItems.map((item) => (
              <div key={item._id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
                {/* Image Preview */}
                <div className="relative aspect-video bg-gray-100">
                  <img
                    src={getThumbnailUrl(item.image)}
                    alt={item.alt}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Status Indicators */}
                  <div className="absolute top-2 left-2 flex gap-2">
                    {item.type === 'video' && (
                      <div className="bg-black bg-opacity-75 text-white p-1 rounded text-xs flex items-center gap-1">
                        <Video className="w-3 h-3" />
                        Video
                      </div>
                    )}
                    {item.featured && (
                      <div className="bg-yellow-500 text-white p-1 rounded text-xs font-medium">
                        Featured
                      </div>
                    )}
                    {!item.isActive && (
                      <div className="bg-red-500 text-white p-1 rounded text-xs font-medium">
                        Inactive
                      </div>
                    )}
                  </div>

                  {/* Selection Checkbox */}
                  <div className="absolute top-2 right-2">
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(item._id)}
                      onChange={() => handleSelectItem(item._id)}
                      className="w-4 h-4 text-primary-purple bg-white border-gray-300 rounded focus:ring-primary-purple"
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center opacity-0 hover:opacity-100">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPreviewItem(item)}
                        className="bg-white text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                        title="Preview"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(item)}
                        className="bg-white text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(item._id)}
                        className="bg-red-600 text-white p-2 rounded-lg hover:bg-red-700 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-1 truncate">{item.title}</h3>
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">{item.description}</p>
                  
                  {/* Category */}
                  <span className="inline-flex px-2 py-1 text-xs font-medium bg-primary-purple/10 text-primary-purple rounded-full mb-2">
                    {item.category}
                  </span>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-1 mb-3">
                    {item.tags.slice(0, 3).map((tag, index) => (
                      <span key={index} className="inline-flex px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                        #{tag}
                      </span>
                    ))}
                    {item.tags.length > 3 && (
                      <span className="inline-flex px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                        +{item.tags.length - 3}
                      </span>
                    )}
                  </div>

                  {/* Meta Info */}
                  <div className="text-xs text-gray-500 space-y-1">
                    <div className="flex justify-between">
                      <span>Size: {item.size}</span>
                      <span>{item.dimensions}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Uploaded: {item.uploadDate || new Date(item.createdAt).toLocaleDateString()}</span>
                      <span>By: {item.createdBy?.name || item.createdBy?.username || 'Unknown'}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* List View */
          <div className="space-y-4">
            {filteredItems.map((item) => (
              <div key={item._id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center gap-4">
                <input
                  type="checkbox"
                  checked={selectedItems.includes(item._id)}
                  onChange={() => handleSelectItem(item._id)}
                  className="w-4 h-4 text-primary-purple bg-white border-gray-300 rounded focus:ring-primary-purple"
                />
                
                <div className="w-20 h-20 flex-shrink-0">
                  <img
                    src={getThumbnailUrl(item.image)}
                    alt={item.alt}
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
                
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">{item.title}</h3>
                  <p className="text-sm text-gray-600 truncate">{item.description}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                    <span>{item.category}</span>
                    <span>{item.size}</span>
                    <span>{item.uploadDate}</span>
                    <span className={`px-2 py-1 rounded-full ${item.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {item.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPreviewItem(item)}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                    title="Preview"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleEdit(item)}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(item._id)}
                    className="p-2 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-lg"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {filteredItems.length === 0 && (
          <div className="text-center py-12">
            <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No media found</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || categoryFilter !== 'all' 
                ? 'Try adjusting your search or filters' 
                : 'Upload your first media file to get started'}
            </p>
            <button
              onClick={handleCreate}
              className="bg-primary-purple hover:bg-primary-magenta text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Add New Media
            </button>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingItem ? 'Edit Media' : 'Add New Media'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* File Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Media File *
                  </label>
                  {formData.url ? (
                    <div className="relative">
                      <img
                        src={formData.url}
                        alt="Preview"
                        className="w-full h-48 object-cover rounded-lg"
                      />
                      <button
                        onClick={() => setFormData(prev => ({ ...prev, url: '' }))}
                        className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full hover:bg-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <FileUpload onFileSelect={handleFileUpload} />
                  )}
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                    placeholder="Enter media title"
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                  >
                    {categories.slice(1).map(category => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                    placeholder="Enter description"
                  />
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags
                  </label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                    placeholder="Enter tags separated by commas"
                  />
                  <p className="text-xs text-gray-500 mt-1">Separate tags with commas (e.g., sports, students, competition)</p>
                </div>

                {/* Alt Text */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Alt Text
                  </label>
                  <input
                    type="text"
                    value={formData.alt}
                    onChange={(e) => setFormData(prev => ({ ...prev, alt: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                    placeholder="Describe the image for accessibility"
                  />
                </div>

                {/* Checkboxes */}
                <div className="flex gap-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                      className="w-4 h-4 text-primary-purple bg-white border-gray-300 rounded focus:ring-primary-purple"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.featured}
                      onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
                      className="w-4 h-4 text-primary-purple bg-white border-gray-300 rounded focus:ring-primary-purple"
                    />
                    <span className="ml-2 text-sm text-gray-700">Featured</span>
                  </label>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="bg-primary-purple hover:bg-primary-magenta text-white px-6 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
                >
                  <Save className="w-4 h-4" />
                  {editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewItem && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">{previewItem.title}</h2>
                <button
                  onClick={() => setPreviewItem(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Image */}
                <div className="w-full">
                  <img
                    src={getImageUrl(previewItem.image)}
                    alt={previewItem.alt}
                    className="w-full h-auto rounded-lg"
                  />
                </div>

                {/* Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Details</h3>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Category:</dt>
                        <dd className="text-gray-900">{previewItem.category}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Type:</dt>
                        <dd className="text-gray-900 capitalize">{previewItem.type}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Size:</dt>
                        <dd className="text-gray-900">{previewItem.size}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Dimensions:</dt>
                        <dd className="text-gray-900">{previewItem.dimensions}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Upload Date:</dt>
                        <dd className="text-gray-900">{previewItem.uploadDate}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Status:</dt>
                        <dd className={`text-sm font-medium ${previewItem.isActive ? 'text-primary-green' : 'text-red-600'}`}>
                          {previewItem.isActive ? 'Active' : 'Inactive'}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Featured:</dt>
                        <dd className={`text-sm font-medium ${previewItem.featured ? 'text-primary-yellow' : 'text-gray-600'}`}>
                          {previewItem.featured ? 'Yes' : 'No'}
                        </dd>
                      </div>
                    </dl>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
                    <p className="text-gray-600 text-sm mb-4">{previewItem.description}</p>
                    
                    <h3 className="font-semibold text-gray-900 mb-2">Tags</h3>
                    <div className="flex flex-wrap gap-1">
                      {previewItem.tags.map((tag, index) => (
                        <span key={index} className="inline-flex px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setPreviewItem(null)
                    handleEdit(previewItem)
                  }}
                  className="bg-primary-purple hover:bg-primary-magenta text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GalleryManagement